import React from 'react';
import { makeStyles, tokens, Button } from '@fluentui/react-components';
import { Attachment, IChatMessage } from '../../libs/models/ChatMessage';
import { AiaChart } from '../utils/AiaChart';
import AiaDataTable from '../utils/AiaDataTable';
import { useAttachment } from '../../contexts/AttachmentContext';
import CalculationGuideButton from '../utils/CalculationGuideButton';

const useClasses = makeStyles({
    container: {
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusXLarge,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
        boxShadow: 'var(--app-shadow-card-subtle)',
        margin: tokens.spacingVerticalL,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '80vh',
        overflow: 'hidden',
    },
    attachment: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        position: 'relative',
        overflow: 'auto',
    },
    chartContainer: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        position: 'relative',
    },
    tableContainer: {
        flex: 1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusXLarge,
        padding: '24px',
    },
    closeButton: {
        background: tokens.colorSubtleBackground,
        color: tokens.colorNeutralForeground2,
        border: '0',
        cursor: 'pointer',
        fontSize: tokens.fontSizeBase400,
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
        minWidth: 'min-content',
        height: 'min-content',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
    },
    headerSection: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '5px',
        marginBottom: '5px',
    },
    guideContainer: {
        flex: 1,
        width: '100%',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
});

interface AttachmentPanelProps {
    attachment: Attachment | null;
    message: IChatMessage | 'closed' | null;
}

export const AttachmentPanel: React.FC<AttachmentPanelProps> = ({ attachment, message }) => {
    const classes = useClasses();
    const { setActiveAttachment } = useAttachment();

    const handleClose = () => {
        setActiveAttachment(null, 'closed');
    };

    if (message === 'closed') {
        return <div className={classes.container}>Attachment panel is closed</div>;
    }

    if (!attachment) {
        return <div className={classes.container}>No attachment to display</div>;
    }

    const renderAttachment = () => {
        switch (attachment.attachmentType) {
            case 'table':
                return (
                    <div className={classes.tableContainer}>
                        <AiaDataTable attachment={attachment} />
                    </div>
                );
            case 'chart':
                return (
                    <div className={classes.chartContainer}>
                        <AiaChart attachment={attachment} />
                    </div>
                );
            default:
                return <div className={classes.attachment}>Unsupported attachment type</div>;
        }
    };

    return (
        <div className={classes.container}>
            <div className={classes.headerSection}>
                <div className={classes.titleRow}>
                    <h3>{attachment.name}</h3>
                    <Button className={classes.closeButton} onClick={handleClose}>
                        <span aria-label="Close">&times;</span>
                    </Button>
                </div>
                <div className={classes.guideContainer}>
                    <CalculationGuideButton attachment={attachment} />
                </div>
            </div>
            {renderAttachment()}
        </div>
    );
};
